import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  ArrowLeft, Download, Copy, Mail, RefreshCw, ExternalLink, CheckCircle2,
  TrendingUp, TrendingDown, Target, BarChart3, Zap, Building2, Settings,
  Send, Paperclip, AlertCircle, FileText, Edit3, Save, X, Loader2,
  Calendar, Bot, Search, Lightbulb, ShieldCheck, AlertTriangle
} from "lucide-react";

import ScoreDistributionChart from "@/components/ScoreDistributionChart";
import { getScoreBand } from "@/lib/scoreBands";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLAR_COLORS = [
  "#2563eb", // Action Blue
  "#10b981", // Success Green
  "#8b5cf6", // Innovation Purple
  "#005EB8", // Royal Blue
  "#00205B"  // Navy
];

const PILLAR_ICONS = [
  Target,
  TrendingUp,
  Zap,
  Building2,
  Settings
];

export default function Results() {
  const { deliverableId } = useParams();
  const [deliverable, setDeliverable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regeneratingEmail, setRegeneratingEmail] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const deliverableRef = useRef(null);

  // Editable email state
  const [isEditing, setIsEditing] = useState(false);
  const [editedRecipient, setEditedRecipient] = useState("");
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [emailLogs, setEmailLogs] = useState([]);
  const [refreshingLogs, setRefreshingLogs] = useState(false);

  const refreshEmailLogs = async () => {
    setRefreshingLogs(true);
    try {
      const logsResponse = await axios.get(`${API}/email/logs/${deliverableId}`);
      setEmailLogs(logsResponse.data || []);
      toast.success("Email status refreshed!");
    } catch (e) {
      toast.error("Failed to refresh email status");
    } finally {
      setRefreshingLogs(false);
    }
  };

  useEffect(() => {
    const fetchDeliverable = async () => {
      // Guard against undefined or invalid deliverableId
      if (!deliverableId || deliverableId === "undefined") {
        toast.error("Invalid deliverable ID");
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/deliverables/${deliverableId}`);
        setDeliverable(response.data);
        // Initialize email editing state
        setEditedRecipient(response.data.contact_email || "");
        setEditedSubject(response.data.email?.subject || "");
        setEditedBody(response.data.email?.body || "");
        
        // Fetch email logs for tracking status
        try {
          const logsResponse = await axios.get(`${API}/email/logs/${deliverableId}`);
          setEmailLogs(logsResponse.data || []);
        } catch (e) {
          // Ignore error if no logs exist
        }
      } catch (error) {
        toast.error("Failed to fetch deliverable");
      } finally {
        setLoading(false);
      }
    };

    fetchDeliverable();
  }, [deliverableId]);

  const handleCopyEmail = () => {
    if (deliverable?.email) {
      const emailText = `Subject: ${editedSubject || deliverable.email.subject}\n\n${editedBody || deliverable.email.body}`;
      navigator.clipboard.writeText(emailText);
      toast.success("Email copied to clipboard!");
    }
  };

  const handleRegenerateEmail = async () => {
    setRegeneratingEmail(true);
    try {
      const response = await axios.post(`${API}/email/regenerate`, {
        deliverable_id: deliverableId
      });
      setDeliverable(prev => ({ ...prev, email: response.data }));
      setEditedSubject(response.data.subject || "");
      setEditedBody(response.data.body || "");
      toast.success("Email regenerated!");
    } catch (error) {
      toast.error("Failed to regenerate email");
    } finally {
      setRegeneratingEmail(false);
    }
  };

  const handleSaveEmailEdits = async () => {
    try {
      await axios.post(`${API}/email/update`, {
        deliverable_id: deliverableId,
        to_email: editedRecipient,
        subject: editedSubject,
        body: editedBody
      });
      setDeliverable(prev => ({
        ...prev,
        contact_email: editedRecipient,
        email: { ...prev.email, subject: editedSubject, body: editedBody }
      }));
      setIsEditing(false);
      toast.success("Email saved!");
    } catch (error) {
      toast.error("Failed to save email");
    }
  };

  const handleGeneratePDF = async () => {
    setGeneratingPDF(true);
    try {
      const response = await axios.post(`${API}/deliverables/${deliverableId}/generate-pdf`);
      setPdfGenerated(true);
      toast.success("PDF generated successfully!");
    } catch (error) {
      toast.error("Failed to generate PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = async () => {
    setExportingPDF(true);
    try {
      // First ensure PDF is generated
      if (!pdfGenerated) {
        await axios.post(`${API}/deliverables/${deliverableId}/generate-pdf`);
        setPdfGenerated(true);
      }

      // Download the PDF
      const response = await axios.get(`${API}/deliverables/${deliverableId}/download-pdf`, {
        responseType: 'blob'
      });

      // Build a sanitized filename using company name from scores or deliverable
      const rawName = scores?.company_name || deliverable?.company_name || 'Report';
      const safeName = rawName.trim().replace(/[^a-zA-Z0-9_\-\s]/g, '').replace(/\s+/g, '_');
      const filename = `Revenue_Acceleration_Score_${safeName}.pdf`;

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Revoke after a short delay — revoking immediately can break the download
      // in some browsers before they've finished reading the blob
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);

      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setExportingPDF(false);
    }
  };

  const handleSendEmail = async () => {
    if (!editedRecipient) {
      toast.error("Please enter a recipient email address");
      return;
    }

    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/email/send`, {
        deliverable_id: deliverableId,
        to_email: editedRecipient,
        subject: editedSubject,
        body: editedBody,
        attach_pdf: attachPdf
      });
      toast.success(`Email sent successfully to ${editedRecipient}!`);
      
      // Refresh email logs to show the new email
      try {
        const logsResponse = await axios.get(`${API}/email/logs/${deliverableId}`);
        setEmailLogs(logsResponse.data || []);
      } catch (e) {
        // Ignore error
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send email";
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-[#005EB8] border-t-transparent animate-spin mx-auto mb-4"></div>
          <p className="text-[#6b7280]">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!deliverable) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#374151] font-semibold mb-4">Deliverable not found</p>
          <Link to="/">
            <Button className="btn-primary">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const scores = deliverable.scores || {};
  const scoreBand = getScoreBand(scores.final_score || 0);
  const hasContactEmail = !!deliverable.contact_email;
  const allEmails = deliverable.all_emails || [];

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-[#005EB8] hover:text-[#00205B] transition-colors" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back to Home</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button 
                onClick={handleDownloadPDF} 
                disabled={exportingPDF}
                className="btn-secondary"
                data-testid="export-pdf-button"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportingPDF ? "Exporting..." : "Export PDF"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Score Hero */}
        <Card className="card overflow-hidden mb-8 w-full">
          {/* Hero Header with Gradient */}
          <div className="bg-gradient-to-r from-[#00205B] via-[#005EB8] to-[#00205B] px-8 py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm text-blue-200 mb-1">Revenue Acceleration Score for</p>
                <h1 className="text-2xl md:text-3xl font-semibold text-white" data-testid="company-name">
                  {scores.company_name || deliverable.company_name}
                </h1>
                <a 
                  href={scores.website || deliverable.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-200 hover:text-white inline-flex items-center gap-1 mt-1 transition-colors"
                >
                  {scores.website || deliverable.url}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="flex items-center gap-6">
                {/* Score Circle */}
                <div className="relative">
                  <div 
                    className="w-28 h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center"
                    style={{
                      background: `conic-gradient(${scoreBand.color} ${(scores.final_score || 0)}%, rgba(255,255,255,0.2) ${(scores.final_score || 0)}%)`
                    }}
                  >
                    <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-white flex flex-col items-center justify-center">
                      <span 
                        className="text-4xl md:text-5xl font-bold"
                        style={{ color: scoreBand.color }}
                        data-testid="final-score"
                      >
                        {scores.final_score || 0}
                      </span>
                      <span className="text-xs text-[#6b7280] font-medium">out of 100</span>
                    </div>
                  </div>
                </div>
                <div className="text-right hidden md:block">
                  <Badge 
                    className="text-sm py-2 px-5 font-semibold"
                    style={{ backgroundColor: scoreBand.color, color: 'white' }}
                    data-testid="score-band"
                  >
                    {scoreBand.label}
                  </Badge>
                </div>
              </div>
            </div>
            {/* Mobile Badge */}
            <div className="md:hidden mt-4">
              <Badge 
                className="text-sm py-2 px-5 font-semibold"
                style={{ backgroundColor: scoreBand.color, color: 'white' }}
              >
                {scoreBand.label}
              </Badge>
            </div>
          </div>

          {/* Where this score sits vs. every company scanned */}
          <ScoreDistributionChart score={scores.final_score || 0} className="px-8 pt-6 pb-4 border-b border-[#e2e8f0]" />

          {/* Strengths and Gaps */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-[#f0fdf4] rounded-lg p-5 border border-[#bbf7d0]">
                <h3 className="text-xs font-semibold text-[#166534] uppercase tracking-[0.1em] mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Top Strengths
                </h3>
                <ul className="space-y-3">
                  {(scores.top_strengths || []).map((strength, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm text-[#166534]" data-testid={`strength-${index}`}>
                      <CheckCircle2 className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#fffbeb] rounded-lg p-5 border border-[#fde68a]">
                <h3 className="text-xs font-semibold text-[#92400e] uppercase tracking-[0.1em] mb-4 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4" />
                  Top Gaps
                </h3>
                <ul className="space-y-3">
                  {(scores.top_gaps || []).map((gap, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm text-[#92400e]" data-testid={`gap-${index}`}>
                      <span className="w-5 h-5 rounded-full bg-[#f59e0b] flex-shrink-0 mt-0.5 flex items-center justify-center text-white text-xs font-bold">!</span>
                      <span>{gap}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="pillars" data-testid="results-tabs" className="w-full">
          <TabsList className="mb-6 w-full">
            <TabsTrigger value="pillars" data-testid="results-tab-pillars">
              <BarChart3 className="w-4 h-4 mr-2" />
              Pillar Breakdown
            </TabsTrigger>
            <TabsTrigger value="gen-seo" data-testid="results-tab-gen-seo">
              <Bot className="w-4 h-4 mr-2" />
              AI Search
            </TabsTrigger>
            <TabsTrigger value="deliverable" data-testid="results-tab-deliverable">
              <Download className="w-4 h-4 mr-2" />
              Full Deliverable
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="results-tab-email">
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </TabsTrigger>
          </TabsList>

          {/* Pillars Tab */}
          <TabsContent value="pillars" data-testid="pillars-panel" className="w-full">
            <div className="space-y-6">
              {(scores.pillars || []).map((pillar, pillarIndex) => {
                const PillarIcon = PILLAR_ICONS[pillarIndex] || Target;
                const pillarColor = PILLAR_COLORS[pillarIndex];
                
                return (
                  <Card key={pillarIndex} className="card overflow-hidden" data-testid={`pillar-card-${pillarIndex}`}>
                    <div className="p-6 border-b border-[#e2e8f0]" style={{ borderLeftWidth: 4, borderLeftColor: pillarColor }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${pillarColor}15` }}
                          >
                            <PillarIcon className="w-5 h-5" style={{ color: pillarColor }} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#00205B]">{pillar.name}</h3>
                            <p className="text-sm text-[#6b7280]">Pillar {pillarIndex + 1} of 5</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-semibold" style={{ color: pillarColor }}>
                            {pillar.total}
                          </div>
                          <div className="text-sm text-[#6b7280]">/20</div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Progress 
                          value={(pillar.total / 20) * 100} 
                          className="h-2 bg-[#171717]/20 [&>div]:bg-[#171717]"
                          style={{ '--progress-foreground': pillarColor }}
                        />
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      {(pillar.categories || []).map((category, catIndex) => (
                        <div key={catIndex} className="flex items-start justify-between gap-4" data-testid={`category-${pillarIndex}-${catIndex}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-[#374151]">{category.name}</span>
                              <Badge 
                                variant="outline" 
                                className="text-xs"
                                style={{ borderColor: pillarColor, color: pillarColor }}
                              >
                                {category.score}/4
                              </Badge>
                            </div>
                            <p className="text-sm text-[#6b7280]">{category.justification}</p>
                            {category.improvement_tip && (
                              <div className="mt-2 p-3 bg-[#fffbeb] border border-[#fde68a] rounded-md">
                                <p className="text-sm text-[#92400e]">
                                  <span className="font-medium">💡 How to improve: </span>
                                  {category.improvement_tip}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex-shrink-0 w-24">
                            <Progress 
                              value={(category.score / 4) * 100} 
                              className="h-2 bg-[#171717]/20 [&>div]:bg-[#171717]"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* AI Search / Generative SEO Tab */}
          <TabsContent value="gen-seo" data-testid="gen-seo-panel" className="w-full">
            {scores.gen_seo ? (() => {
              const gs = scores.gen_seo;
              const isCrawlBlocked = gs.crawl_blocked === true;
              const hasScore = gs.overall_llm_visibility !== null && gs.overall_llm_visibility !== undefined;

              // Crawl was blocked AND no external signals — nothing to score from.
              // Show an honest "could not analyze" instead of a misleading 0/10.
              if (isCrawlBlocked && !hasScore) {
                return (
                  <Card className="card p-8 text-center" data-testid="gen-seo-unavailable">
                    <AlertTriangle className="w-10 h-10 text-[#f59e0b] mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-[#92400e] mb-2">AI Search visibility could not be analyzed</h3>
                    <p className="text-sm text-[#6b7280] max-w-2xl mx-auto leading-relaxed">
                      {gs.verdict || "The website crawl was blocked by bot protection and no third-party signals were found to substitute. This is a crawler limitation, not a reflection of the company's actual AI search visibility — sites behind Cloudflare/Akamai are often perfectly visible to ChatGPT, Claude, and Perplexity. Retry the scan or verify the company manually."}
                    </p>
                  </Card>
                );
              }

              const rawScore = hasScore ? gs.overall_llm_visibility : 0;
              // Records scored before the 100-point switch lack score_scale and hold 0-10 values
              const llmScore = gs.score_scale === 100 ? rawScore : rawScore * 10;
              const llmPct = Math.min(llmScore, 100);
              const scoreColor = llmScore >= 70 ? "#16a34a" : llmScore >= 40 ? "#f59e0b" : "#ef4444";
              const priorityColors = { High: "bg-red-100 text-red-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-100 text-slate-600" };
              const categoryColors = { Content: "#005EB8", Structure: "#8b5cf6", Authority: "#10b981", Entity: "#f59e0b", Distribution: "#0ea5e9" };

              return (
                <div className="space-y-6">
                  {/* Banner: scored from third-party signals because crawl was blocked */}
                  {isCrawlBlocked && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="gen-seo-crawl-blocked-banner">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-900">
                        <p className="font-semibold mb-1">Website crawl was blocked by bot protection</p>
                        <p className="leading-relaxed">
                          The site is behind a Cloudflare/Akamai-style challenge that our crawler could not bypass. The score below is based on <strong>third-party signals</strong> (press, directory listings, leadership presence) rather than the site's own content. The actual LLMs (ChatGPT/Claude/Perplexity) likely see this company normally.
                        </p>
                      </div>
                    </div>
                  )}
                  {/* Score hero card */}
                  <Card className="card overflow-hidden">
                    <div className="p-6 border-b border-[#e2e8f0]" style={{ background: "linear-gradient(to right, #00205B, #005EB8, #00205B)" }}>
                      <div className="flex items-center gap-3 mb-1">
                        <Bot className="w-5 h-5 text-blue-200" />
                        <h2 className="text-white font-semibold text-lg">Generative SEO — AI Search Visibility</h2>
                      </div>
                      <p className="text-blue-200 text-sm">How likely this company is to be cited or recommended by Claude, ChatGPT, Perplexity, and Gemini</p>
                    </div>
                    <div className="p-6 flex items-start gap-6">
                      <div className="flex-shrink-0 text-center">
                        <div className="text-5xl font-bold" style={{ color: scoreColor }}>{llmScore}</div>
                        <div className="text-xs text-[#6b7280] mt-1">out of 100</div>
                        <div className="text-xs font-semibold mt-1" style={{ color: scoreColor }}>
                          {llmScore >= 70 ? "Strong Visibility" : llmScore >= 40 ? "Moderate Visibility" : "Low Visibility"}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="w-full bg-[#e2e8f0] rounded-full h-2 mb-3">
                          <div className="h-2 rounded-full transition-all" style={{ width: `${llmPct}%`, background: scoreColor }} />
                        </div>
                        <p className="text-sm text-[#374151] leading-relaxed">{gs.verdict}</p>
                      </div>
                    </div>
                  </Card>

                  {/* Dimension breakdown — absent on reports generated before dimension scoring shipped */}
                  {(gs.dimension_scores || []).length > 0 && (
                    <Card className="card p-5" data-testid="gen-seo-dimension-breakdown">
                      <h3 className="text-xs font-semibold text-[#6b7280] uppercase tracking-[0.1em] mb-4">How the Score Breaks Down</h3>
                      <div className="space-y-4">
                        {gs.dimension_scores.map((d, i) => {
                          const dScore = Math.min(d.score || 0, 100);
                          const dColor = dScore >= 70 ? "#16a34a" : dScore >= 40 ? "#f59e0b" : "#ef4444";
                          return (
                            <div key={i}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold text-[#1e293b]">{d.dimension}</span>
                                <span className="text-sm font-bold" style={{ color: dColor }}>
                                  {dScore}<span className="font-normal text-[#9ca3af]">/100</span>
                                </span>
                              </div>
                              <div className="w-full bg-[#e2e8f0] rounded-full h-1.5 mb-1.5">
                                <div className="h-1.5 rounded-full transition-all" style={{ width: `${dScore}%`, background: dColor }} />
                              </div>
                              <p className="text-xs text-[#6b7280] leading-relaxed">{d.rationale}</p>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  )}

                  {/* Strengths + Gaps */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="card p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <ShieldCheck className="w-4 h-4 text-[#16a34a]" />
                        <h3 className="text-xs font-semibold text-[#166534] uppercase tracking-[0.1em]">LLM Visibility Strengths</h3>
                      </div>
                      <ul className="space-y-2">
                        {(gs.strengths || []).map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#166534]">
                            <CheckCircle2 className="w-4 h-4 text-[#22c55e] flex-shrink-0 mt-0.5" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                    <Card className="card p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <AlertTriangle className="w-4 h-4 text-[#f59e0b]" />
                        <h3 className="text-xs font-semibold text-[#92400e] uppercase tracking-[0.1em]">LLM Visibility Gaps</h3>
                      </div>
                      <ul className="space-y-2">
                        {(gs.gaps || []).map((g, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#92400e]">
                            <TrendingDown className="w-4 h-4 text-[#f59e0b] flex-shrink-0 mt-0.5" />
                            <span>{g}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  </div>

                  {/* Recommendations */}
                  <Card className="card p-6">
                    <div className="flex items-center gap-2 mb-5">
                      <Lightbulb className="w-4 h-4 text-[#005EB8]" />
                      <h3 className="text-sm font-semibold text-[#00205B]">How to Rank Better in AI Search</h3>
                    </div>
                    <div className="space-y-3">
                      {(gs.recommendations || []).map((rec, i) => {
                        const catColor = categoryColors[rec.category] || "#6b7280";
                        const priBadge = priorityColors[rec.priority] || "bg-slate-100 text-slate-600";
                        return (
                          <div key={i} className="rounded-lg border border-[#e2e8f0] p-4" style={{ borderLeftWidth: "3px", borderLeftColor: catColor }}>
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <span className="font-semibold text-[#1e293b] text-sm">{rec.title}</span>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${priBadge}`}>{rec.priority}</span>
                              <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: `${catColor}22`, color: catColor }}>{rec.category}</span>
                            </div>
                            <p className="text-sm text-[#6b7280] leading-relaxed">{rec.description}</p>
                          </div>
                        );
                      })}
                    </div>
                  </Card>

                  {/* Example queries */}
                  <Card className="card p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Search className="w-4 h-4 text-[#6b7280]" />
                      <h3 className="text-sm font-semibold text-[#374151]">Queries This Company Should Appear In</h3>
                    </div>
                    <div className="space-y-2">
                      {(gs.llm_search_queries || []).map((q, i) => (
                        <div key={i} className="bg-[#f8fafc] border border-[#e2e8f0] rounded-md px-4 py-2.5 text-sm text-[#374151] italic">
                          "{q}"
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              );
            })() : (
              <Card className="card p-8 text-center">
                <Bot className="w-10 h-10 text-[#6b7280] mx-auto mb-3" />
                <p className="text-sm text-[#6b7280]">AI Search analysis not available for this report. Re-run the analysis to generate it.</p>
              </Card>
            )}
          </TabsContent>

          {/* Deliverable Tab */}
          <TabsContent value="deliverable" data-testid="deliverable-panel" className="w-full">
            <Card className="card overflow-hidden w-full" style={{ maxWidth: 'none' }}>
              <div className="p-4 bg-[#f8fafc] border-b border-[#e2e8f0] flex items-center justify-between">
                <span className="text-sm text-[#6b7280]">IGTMS Branded Deliverable</span>
                <Button 
                  onClick={handleDownloadPDF} 
                  disabled={exportingPDF}
                  className="btn-primary"
                  size="sm"
                  data-testid="export-pdf-button-inline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportingPDF ? "Exporting..." : "Export PDF"}
                </Button>
              </div>
              <div 
                ref={deliverableRef}
                className="bg-white w-full"
                style={{ padding: 0 }}
                dangerouslySetInnerHTML={{ __html: deliverable.html }}
                data-testid="deliverable-content"
              />
            </Card>
          </TabsContent>

          {/* Email Tab - Enhanced with Send Functionality */}
          <TabsContent value="email" data-testid="email-panel" className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Email Composer - Takes 2 columns */}
              <Card className="card p-6 lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-[#00205B]">Compose Outreach Email</h3>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <Button 
                          onClick={() => setIsEditing(false)}
                          variant="ghost"
                          size="sm"
                          className="text-[#6b7280]"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleSaveEmailEdits}
                          size="sm"
                          className="btn-primary"
                        >
                          <Save className="w-4 h-4 mr-1" />
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          onClick={() => setIsEditing(true)}
                          variant="ghost"
                          size="sm"
                          className="text-[#005EB8]"
                          data-testid="edit-email-button"
                        >
                          <Edit3 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          onClick={handleRegenerateEmail}
                          disabled={regeneratingEmail}
                          variant="ghost"
                          size="sm"
                          className="text-[#005EB8]"
                          data-testid="regenerate-email-button"
                        >
                          <RefreshCw className={`w-4 h-4 mr-1 ${regeneratingEmail ? 'animate-spin' : ''}`} />
                          {regeneratingEmail ? "Regenerating..." : "Regenerate"}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Recipient Email */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="recipient" className="text-sm font-medium text-[#374151] mb-2 flex items-center gap-2">
                      Recipient Email
                      {!hasContactEmail && !editedRecipient && (
                        <span className="text-xs bg-[#fef3c7] text-[#92400e] px-2 py-0.5 rounded-full flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          No email found on website
                        </span>
                      )}
                    </Label>
                    <Input
                      id="recipient"
                      type="email"
                      value={editedRecipient}
                      onChange={(e) => setEditedRecipient(e.target.value)}
                      placeholder="Enter recipient email address"
                      disabled={!isEditing}
                      className={!isEditing ? "bg-[#f8fafc]" : ""}
                      data-testid="recipient-input"
                    />
                    {allEmails.length > 0 && isEditing && (
                      <div className="mt-2">
                        <p className="text-xs text-[#6b7280] mb-1">Emails found on website:</p>
                        <div className="flex flex-wrap gap-2">
                          {allEmails.map((email) => (
                            <button
                              key={email}
                              type="button"
                              onClick={() => setEditedRecipient(email)}
                              className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                editedRecipient === email 
                                  ? 'bg-[#005EB8] text-white border-[#005EB8]' 
                                  : 'bg-white text-[#374151] border-[#e2e8f0] hover:border-[#005EB8] hover:text-[#005EB8]'
                              }`}
                              data-testid={`email-suggestion-${email}`}
                            >
                              {email}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {!editedRecipient && (
                      <p className="text-xs text-[#6b7280] mt-1">Enter the email address to send the Revenue Acceleration Score report to.</p>
                    )}
                  </div>

                  {/* Subject Line */}
                  <div>
                    <Label htmlFor="subject" className="text-sm font-medium text-[#374151] mb-2">Subject Line</Label>
                    <Input
                      id="subject"
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      placeholder="Email subject"
                      disabled={!isEditing}
                      className={!isEditing ? "bg-[#f8fafc]" : ""}
                      data-testid="email-subject-input"
                    />
                  </div>

                  {/* Email Body */}
                  <div>
                    <Label htmlFor="body" className="text-sm font-medium text-[#374151] mb-2">Email Body</Label>
                    <Textarea
                      id="body"
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      placeholder="Email content"
                      rows={12}
                      disabled={!isEditing}
                      className={`text-sm leading-relaxed ${!isEditing ? "bg-[#f8fafc]" : ""}`}
                      data-testid="email-body-input"
                    />
                  </div>

                  {/* PDF Attachment Toggle */}
                  <div className="flex items-center justify-between p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
                    <div className="flex items-center gap-3">
                      <Paperclip className="w-5 h-5 text-[#005EB8]" />
                      <div>
                        <p className="font-medium text-[#374151]">Attach Revenue Acceleration Score PDF</p>
                        <p className="text-xs text-[#6b7280]">Include the full report as a PDF attachment</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={attachPdf}
                        onChange={(e) => setAttachPdf(e.target.checked)}
                        className="sr-only peer"
                        data-testid="attach-pdf-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#005EB8]"></div>
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-[#e2e8f0]">
                  <Button 
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !editedRecipient}
                    className="btn-primary flex-1 sm:flex-none"
                    data-testid="send-email-button"
                  >
                    {sendingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={handleCopyEmail}
                    variant="outline"
                    className="flex-1 sm:flex-none"
                    data-testid="copy-email-button"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Email
                  </Button>
                  <Button 
                    onClick={() => {
                      const mailtoLink = `mailto:${editedRecipient}?subject=${encodeURIComponent(editedSubject)}&body=${encodeURIComponent(editedBody)}`;
                      window.open(mailtoLink);
                    }}
                    variant="outline"
                    className="flex-1 sm:flex-none"
                    data-testid="open-email-client-button"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in Client
                  </Button>
                </div>

                {/* Link to Email Campaigns */}
                <div className="mt-6 pt-6 border-t border-[#e2e8f0]">
                  <div className="text-center py-4 bg-[#f0f9ff] rounded-lg border border-[#bae6fd]">
                    <Calendar className="w-8 h-8 text-[#005EB8] mx-auto mb-2" />
                    <p className="text-sm text-[#0369a1] mb-3">Want to schedule follow-up emails?</p>
                    <Link to={`/email-campaigns?deliverable=${deliverableId}`}>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-[#005EB8] text-[#005EB8] hover:bg-[#005EB8] hover:text-white"
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Manage Email Campaigns
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>

              {/* Sidebar - PDF Preview & Quick Actions */}
              <Card className="card p-6">
                <h3 className="text-lg font-semibold text-[#00205B] mb-4">Attachment Preview</h3>
                
                {/* PDF Card */}
                <div className="bg-[#f8fafc] rounded-lg border border-[#e2e8f0] p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-[#dc2626] rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#374151] truncate">Revenue_Acceleration_Score_{((scores?.company_name || deliverable?.company_name || 'Report').trim().replace(/[^a-zA-Z0-9_\-\s]/g, '').replace(/\s+/g, '_'))}.pdf</p>
                      <p className="text-xs text-[#6b7280]">Revenue Acceleration Score Report</p>
                      {pdfGenerated ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[#166534] mt-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Ready to attach
                        </span>
                      ) : (
                        <span className="text-xs text-[#6b7280] mt-1">Will be generated on send</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-3">
                  <Button 
                    onClick={handleGeneratePDF}
                    disabled={generatingPDF || pdfGenerated}
                    variant="outline"
                    className="w-full justify-start"
                    data-testid="generate-pdf-button"
                  >
                    {generatingPDF ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating PDF...
                      </>
                    ) : pdfGenerated ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2 text-[#22c55e]" />
                        PDF Generated
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Generate PDF Now
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={handleDownloadPDF}
                    disabled={exportingPDF}
                    variant="outline"
                    className="w-full justify-start"
                    data-testid="download-pdf-sidebar-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exportingPDF ? "Downloading..." : "Download PDF"}
                  </Button>
                </div>

                {/* Help Text */}
                <div className="mt-6 p-4 bg-[#f0f9ff] rounded-lg border border-[#bae6fd]">
                  <h4 className="text-sm font-medium text-[#0369a1] mb-2">Email Tips</h4>
                  <ul className="text-xs text-[#0369a1] space-y-1">
                    <li>• Edit the email content to personalize your outreach</li>
                    <li>• The PDF attachment contains the full Revenue Acceleration Score report</li>
                    <li>• Emails are sent from brad@theaiintegrationhub.com</li>
                  </ul>
                </div>

                {/* Email History with Tracking */}
                {emailLogs.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-[#00205B]">Email History</h4>
                      <Button
                        onClick={refreshEmailLogs}
                        disabled={refreshingLogs}
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-[#005EB8]"
                        data-testid="refresh-email-logs"
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${refreshingLogs ? 'animate-spin' : ''}`} />
                        {refreshingLogs ? 'Refreshing...' : 'Refresh'}
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {emailLogs.slice(0, 5).map((log, index) => (
                        <div 
                          key={log.id || index} 
                          className="bg-[#f8fafc] rounded-lg border border-[#e2e8f0] p-3"
                          data-testid={`email-log-${index}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-[#6b7280]">
                              {new Date(log.sent_at).toLocaleDateString()} {new Date(log.sent_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </span>
                            {log.status === 'sent' || log.status === 'delivered' ? (
                              <span className="inline-flex items-center gap-1 text-xs text-[#166534] bg-[#dcfce7] px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="w-3 h-3" />
                                {log.status === 'delivered' ? 'Delivered' : 'Sent'}
                              </span>
                            ) : log.status === 'bounced' ? (
                              <span className="inline-flex items-center gap-1 text-xs text-[#dc2626] bg-[#fee2e2] px-2 py-0.5 rounded-full">
                                <AlertCircle className="w-3 h-3" />
                                Bounced
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-[#92400e] bg-[#fef3c7] px-2 py-0.5 rounded-full">
                                <AlertCircle className="w-3 h-3" />
                                {log.status}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#374151] truncate mb-2">{log.to_email}</p>
                          
                          {/* Tracking Status */}
                          <div className="flex items-center gap-4 text-xs">
                            <div className={`flex items-center gap-1 ${log.opened ? 'text-[#166534]' : 'text-[#6b7280]'}`}>
                              {log.opened ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3" />
                                  <span>Opened{log.open_count > 1 ? ` (${log.open_count}x)` : ''}</span>
                                </>
                              ) : (
                                <>
                                  <Mail className="w-3 h-3" />
                                  <span>Not opened</span>
                                </>
                              )}
                            </div>
                            {log.clicked && (
                              <div className="flex items-center gap-1 text-[#166534]">
                                <ExternalLink className="w-3 h-3" />
                                <span>Clicked{log.click_count > 1 ? ` (${log.click_count}x)` : ''}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Opened timestamp */}
                          {log.opened && log.opened_at && (
                            <p className="text-xs text-[#6b7280] mt-1">
                              First opened: {new Date(log.opened_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
